import { useCallback } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Utils } from '@sparrow-marketing/contracts';

import { isEmpty } from 'lodash';
import { IInsights } from '..';
import { useIndexedDB } from '../../hooks';
import { formatAdsFilterData, formatWebsiteFilterData } from './formatter';
import {
  FilterAccountsState,
  SelectedFilterAccountsState,
  FilterInputValueState,
  HideInActiveState,
} from './states';
import { formatIndexDBData } from './utils';

export const FILTER_INDEXED_DB_KEYS = {
  properties: 'properties',
  views: 'views',
  campaigns: 'campaigns',
  adSets: 'adSets',
  ads: 'ads',
  hideInActive: 'hideInActive',
  filterSearch: 'filterSearch',
};

const WEBSITE_INDEXED_DB_KEYS = ['properties', 'views'];
const ADS_INDEXED_DB_KEYS = ['campaigns', 'adSets', 'ads'];

export const useCampaignFilter = () => {
  const { get, update } = useIndexedDB({ currentStore: 'filterCampaign' });
  const setFilterAccounts = useSetRecoilState(FilterAccountsState);
  const [selectedFilterAccounts, setSelectedFilterAccounts] = useRecoilState(
    SelectedFilterAccountsState
  );
  const setSearchValue = useSetRecoilState(FilterInputValueState);
  const setHideInActive = useSetRecoilState(HideInActiveState);

  const fetchFromIndexedDB = useCallback(async () => {
    const promises: Promise<any>[] = Object.values(
      FILTER_INDEXED_DB_KEYS
    ).map(key => get<any>(key));

    const [
      properties,
      views,
      campaigns,
      adSets,
      ads,
      hideInActive,
      filterSearch,
    ] = await Promise.all(promises);

    const formattedData = formatIndexDBData({
      properties,
      views,
      campaigns,
      adSets,
      ads,
    });

    setSelectedFilterAccounts(formattedData);

    setSearchValue(prevState => ({
      ...prevState,
      ...filterSearch,
    }));

    setHideInActive(prevState => ({
      ...prevState,

      ...hideInActive,
    }));
  }, [get, setHideInActive, setSearchValue, setSelectedFilterAccounts]);

  // this function will only update the filter account state we will
  // dump all the required insights data for filter ui to work seamless
  const initializeFilterAccounts = useCallback(
    (insights: IInsights) => {
      const { channel, accountId } = insights;
      let formattedData: Utils.DynamicObject;

      if (/googleAnalytics/gi.test(channel)) {
        formattedData = formatWebsiteFilterData(insights);
      } else {
        formattedData = formatAdsFilterData(insights);
      }

      setFilterAccounts(prevState => ({
        ...prevState,
        [channel]: {
          ...prevState[channel],
          [accountId]: {
            ...formattedData,
          },
        },
      }));
    },
    [setFilterAccounts]
  );

  // this function will only update the selected filter account state
  // we will dump all the selected ids data for filter ui to work
  const initializeWebsiteSelectedFilterAccounts = useCallback(
    async (insights: IInsights, invalidateCache = false) => {
      const { accountId } = insights;

      const websiteFormattedData = formatWebsiteFilterData(insights);

      const promises: Promise<any>[] = [];

      [...WEBSITE_INDEXED_DB_KEYS].forEach(key => promises.push(get<any>(key)));

      const selectedWebsitePropertyIds = websiteFormattedData.properties
        .filter(property => property.isActive)
        .map(property => property.attributes.id.value);

      const selectedWebsiteViewIds = websiteFormattedData.views
        .filter(view => view.isActive)
        .map(view => view.attributes.id.value);

      const webData: Utils.DynamicObject = {};

      const [properties, views] = await Promise.all(promises);

      const formattedData = formatIndexDBData({ properties, views });

      const formattedProperties =
        formattedData?.[accountId]?.properties == null;

      const formattedViews = formattedData?.[accountId]?.views == null;

      if (!formattedData || formattedProperties || invalidateCache) {
        webData[accountId] = webData[accountId] ?? {};
        webData[accountId].properties = webData[accountId].properties ?? {};
        webData[accountId].properties = selectedWebsitePropertyIds;
      }

      if (!formattedData || formattedViews || invalidateCache) {
        webData[accountId] = webData[accountId] ?? {};
        webData[accountId].views = webData[accountId].views ?? {};
        webData[accountId].views = selectedWebsiteViewIds;
      }

      if (
        !invalidateCache &&
        !isEmpty(formattedData) &&
        (!formattedProperties || !formattedViews)
      ) {
        webData[accountId] = {
          ...(webData?.[accountId] ?? {}),
          ...(formattedData?.[accountId] ?? {}),
        };
      }

      const toUpdateWebAccounts = {
        ...selectedFilterAccounts,
        ...webData,
      };

      setSelectedFilterAccounts(toUpdateWebAccounts);

      return toUpdateWebAccounts;
    },
    [get, selectedFilterAccounts, setSelectedFilterAccounts]
  );

  // this function will only update the selected filter account state
  // we will dump all the selected ids data for filter ui to work
  const initializeAdsSelectedFilterAccounts = useCallback(
    async (insights: IInsights, invalidateCache = false) => {
      const { accountId } = insights;
      const adsFormattedData = formatAdsFilterData(insights);

      const promises: Promise<any>[] = [];

      [...ADS_INDEXED_DB_KEYS].forEach(key => promises.push(get<any>(key)));

      const [campaigns, adSets, ads] = await Promise.all(promises);

      const formattedData = formatIndexDBData({ campaigns, adSets, ads });

      const selectedAdsCampaignsIds = adsFormattedData.campaigns
        .filter(campaign => campaign.isActive)
        .map(campaign => campaign.attributes.id?.value);

      const selectedAdSetViewIds = adsFormattedData.adSets
        .filter(adSet => adSet.isActive)
        .map(adSet => adSet.attributes.id.value);

      const selectedAdsViewIds = adsFormattedData.ads
        .filter(ad => ad.isActive)
        .map(ad => ad.attributes.id.value);

      const adsData: Utils.DynamicObject = {};

      const formattedCampaigns = formattedData?.[accountId]?.campaigns == null;
      const formattedAdSets = formattedData?.[accountId]?.adSets == null;
      const formattedAds = formattedData?.[accountId]?.ads == null;

      if (!formattedData || formattedCampaigns || invalidateCache) {
        adsData[accountId] = adsData[accountId] ?? {};
        adsData[accountId].campaigns = adsData[accountId].campaigns ?? {};
        adsData[accountId].campaigns = selectedAdsCampaignsIds;
      }

      if (!formattedData || formattedAdSets || invalidateCache) {
        adsData[accountId] = adsData[accountId] ?? {};
        adsData[accountId].adSets = adsData[accountId].adSets ?? {};
        adsData[accountId].adSets = selectedAdSetViewIds;
      }

      if (!formattedData || formattedAds || invalidateCache) {
        adsData[accountId] = adsData[accountId] ?? {};
        adsData[accountId].ads = adsData[accountId].ads ?? {};
        adsData[accountId].ads = selectedAdsViewIds;
      }

      if (
        !invalidateCache &&
        (!formattedCampaigns || !formattedAdSets || !formattedAds)
      ) {
        adsData[accountId] = {
          ...(adsData?.[accountId] ?? {}),
          ...(formattedData?.[accountId] ?? {}),
        };
      }
      const toUpdateAdsAccounts = {
        ...selectedFilterAccounts,
        ...adsData,
      };

      setSelectedFilterAccounts(toUpdateAdsAccounts);

      return toUpdateAdsAccounts;
    },
    [get, selectedFilterAccounts, setSelectedFilterAccounts]
  );

  const resetIndexedDBForSource = useCallback(
    async (sourceId: string, isWebsite: boolean, insights: IInsights) => {
      //

      const toUpdateIndexedDBKeys = [
        FILTER_INDEXED_DB_KEYS.filterSearch,
        FILTER_INDEXED_DB_KEYS.hideInActive,
      ];

      let dataToBeUpdated: Utils.DynamicObject;

      if (isWebsite) {
        toUpdateIndexedDBKeys.push(...WEBSITE_INDEXED_DB_KEYS);
        dataToBeUpdated = await initializeWebsiteSelectedFilterAccounts(
          insights,
          true
        );
      } else {
        toUpdateIndexedDBKeys.push(...ADS_INDEXED_DB_KEYS);
        dataToBeUpdated = await initializeAdsSelectedFilterAccounts(
          insights,
          true
        );
      }

      const promises: Promise<any>[] = toUpdateIndexedDBKeys.map(
        toUpdateIndexedDBKey => get<any>(toUpdateIndexedDBKey)
      );

      const data = await Promise.all(promises);

      toUpdateIndexedDBKeys.forEach((key, idx) => {
        // skip if is undefined or null
        if (!key || !data[idx]) {
          return;
        }
        if (/hideInActive/gi.test(key) && !isWebsite) {
          data[idx][sourceId] = false;
        } else if (/filterSearch/gi.test(key)) {
          data[idx][sourceId] = '';
        } else {
          data[idx][sourceId] = dataToBeUpdated[sourceId];
        }

        update(data[idx], key);
      });

      setSearchValue(prevState => ({
        ...prevState,
        [sourceId]: '',
      }));

      setHideInActive(prevState => ({
        ...prevState,
        [sourceId]: false,
      }));
    },
    [
      get,
      initializeAdsSelectedFilterAccounts,
      initializeWebsiteSelectedFilterAccounts,
      setHideInActive,
      setSearchValue,
      update,
    ]
  );

  return {
    initializeFilterAccounts,
    initializeWebsiteSelectedFilterAccounts,
    initializeAdsSelectedFilterAccounts,
    fetchFromIndexedDB,
    resetIndexedDBForSource,
  };
};
